import InitialsNavigation from "../../elements/initials-navigation/initials-navigation";

const SELECTORS = {
  search: {
    container: {
      default: "SearchableList-search",
    },
    input: "SearchableList-input",
  },
  initials: {
    container: "SearchableList-initialsNavigation",
  },
  list: {
    container: "SearchableList-list",
    section: {
      default: "List-section",
    },
    letter: "List-letter",
    row: {
      default: "List-row",
      title: "Row-title",
      description: "Row-description",
      link: "Row-link",
    },
  },
  notification: {
    default: "SearchableList-notification",
  },
  utils: {
    hidden: "u-hidden",
  },
};

class SearchableList {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.virtualList = [];
    this.throttleSearch = false;
    this.throttleDuration = 300;

    this._setBindings();
    this._queryElements();
    this._generateVirtualList();
    this._addEventListener();
    this._activateSearch();

    this.initialsNavigation = new InitialsNavigation(
      this.initialsNavigationContainer,
      this._handleInitialsClick
    );
  }

  /**
   * Set context for callbacks
   */
  _setBindings() {
    this._filterListBySearchString = this._filterListBySearchString.bind(this);
    this._handleInitialsClick = this._handleInitialsClick.bind(this);
  }

  /**
   * Query all necessary Elements
   */
  _queryElements() {
    this.searchContainer = this.container.querySelector(
      `.${SELECTORS.search.container.default}`
    );
    this.search = this.container.querySelector(`.${SELECTORS.search.input}`);
    this.list = this.container.querySelector(`.${SELECTORS.list.container}`);
    this.sections = this.list.querySelectorAll(
      `.${SELECTORS.list.section.default}`
    );
    this.notification = this.container.querySelector(
      `.${SELECTORS.notification.default}`
    );
    this.initialsNavigationContainer = this.container.querySelector(
      `.${SELECTORS.initials.container}`
    );
  }

  /**
   * Traverse the list to identify sections as well as rows with
   * title and description
   */
  _generateVirtualList() {
    this.virtualList = [...this.sections].map((section) =>
      SearchableList.generateVirtualSections(section)
    );
  }

  /**
   * Add eventListener for change of input value
   */
  _addEventListener() {
    this.search.addEventListener("input", () =>
      this._throttle(this._filterListBySearchString)
    );
  }

  /**
   * The search is hidden by default to fullfill progressive enhancement.
   * Its just visible if js is available.
   */
  _activateSearch() {
    this.searchContainer.classList.remove(SELECTORS.utils.hidden);
  }

  /**
   * This is called as a callback when the initials class fires the click
   * eventListener. This calls the scroll and focus method.
   *
   * @param {string} sectionId
   */
  _handleInitialsClick(sectionId) {
    const targetSection = [...this.sections].find(
      (section) => section.id === sectionId
    );
    const firstLinkInTargetSection = targetSection.querySelector(
      `.${SELECTORS.list.row.link}`
    );
    SearchableList.scrollToSection(targetSection);
    SearchableList.moveFocusToSection(firstLinkInTargetSection);
  }

  /**
   *  Call a callback after a given delay
   *
   * @param {Function} callback
   */
  _throttle(callback) {
    if (this.throttleSearch) return;
    this.throttleSearch = true;
    setTimeout(() => {
      // trim() just to improve user experience
      callback(this.search.value.trim());
      this.throttleSearch = false;
    }, this.throttleDuration);
  }

  /**
   * The callback for the evenListener triggered by
   * user input. This starts to update the virtualList and
   * syncs that afterwards with the DOM
   *
   * @param {string} searchString
   */
  _filterListBySearchString(searchString) {
    this.virtualList = this.virtualList.map((section) =>
      SearchableList.updateVirtualListSection(section, searchString)
    );
    this._syncDOMwithVirtualList();
  }

  /**
   * Sync the actual DOM with the virtual list and show/hide
   * the notification if necessary
   */
  _syncDOMwithVirtualList() {
    let sectionsHidden = true;
    this.virtualList.forEach((section) => {
      if (section.hidden) {
        section.ref.classList.add(SELECTORS.utils.hidden);
        this.initialsNavigation.hideInitial(section.ref.id);
      } else {
        sectionsHidden = false;
        section.ref.classList.remove(SELECTORS.utils.hidden);
        this.initialsNavigation.showInitial(section.ref.id);
        section.rows.forEach((row) => {
          if (row.hidden) {
            row.ref.closest("li").classList.add(SELECTORS.utils.hidden);
          } else {
            row.ref.closest("li").classList.remove(SELECTORS.utils.hidden);
          }
        });
      }
    });
    this._showNotification(sectionsHidden);
  }

  /**
   * Toggle the notification in case of an empty list
   *
   * @param {boolean} visible
   */
  _showNotification(visible) {
    if (visible) {
      this.notification.classList.remove(SELECTORS.utils.hidden);
    } else {
      this.notification.classList.add(SELECTORS.utils.hidden);
    }
  }

  /**
   * Scroll HTMLElement smooth into the viewport
   * 4 bits offset (asympthotic with decreasing height)
   *
   * @param {HTMLElement} section
   */
  static scrollToSection(section) {
    if (section) {
      window.scrollBy({
        top:
          section.getBoundingClientRect().top -
          /* eslint-disable-next-line no-bitwise */
          (window.innerHeight >> 4),
        left: 0,
        behavior: "smooth",
      });
    }
  }

  /**
   * This sets the focus on a given element without any
   * any scrolling
   *
   * @param {HTMLElement} firstRowInSection
   */
  static moveFocusToSection(firstRowInSection) {
    firstRowInSection.focus({ preventScroll: true });
  }

  /**
   * Filter the current list by searchString and set
   * hidden for sections / rows that do not match
   *
   * @param {object} section
   * @param {string} searchString
   * @returns {object}
   */
  static updateVirtualListSection(section, searchString) {
    let sectionIsHidden = true;
    const updatedRows = {
      rows: section.rows.map((row) => {
        const updatedRow = SearchableList.updateVirtualListRow(
          row,
          searchString
        );
        if (!updatedRow.hidden) {
          sectionIsHidden = false;
        }
        return updatedRow;
      }),
    };
    return { ...section, ...updatedRows, hidden: sectionIsHidden };
  }

  /**
   * Check if this row matches the searchString
   *
   * @param {object} row
   * @param {string} searchString
   * @returns {object}
   */
  static updateVirtualListRow(row, searchString) {
    return row.title.includes(searchString) ||
      row.description.includes(searchString)
      ? { ...row, hidden: false }
      : { ...row, hidden: true };
  }

  /**
   * Generate sections for the virtualList by inspecting the DOM
   *
   * @param {HTMLElement} section
   * @returns {object}
   */
  static generateVirtualSections(section) {
    const rows = section.querySelectorAll(`.${SELECTORS.list.row.default}`);
    return {
      ref: section,
      rows: [...rows].map((row) => {
        return SearchableList.generateVirtualRows(row);
      }),
    };
  }

  /**
   * Generate rows for the virtualList by inspecting the DOM
   *
   * @param {HTMLElement} row
   * @returns {object}
   */
  static generateVirtualRows(row) {
    return {
      ref: row,
      title: row
        .querySelector(`.${SELECTORS.list.row.title}`)
        .innerText.toLowerCase(),
      description: row
        .querySelector(`.${SELECTORS.list.row.description}`)
        .innerText.toLowerCase(),
    };
  }
}

export default SearchableList;
